import { getConfig, LanguageHelp } from './LanguageHelper'

const countryCodeAlias: Record<string, string> = {
  UK: 'GB',
}

const displayNamesCache: Record<string, any> = {}

export type GeoLanguageCountryCountItem = {
  code: string
  count: number
}

export type GeoLanguageCountryOption = {
  key: string
  value: string
  label: string
  count?: number
}

const getUiLocale = (): string => {
  const selectedLang = getConfig()?.selectedLang
  if (selectedLang === 'tr' || selectedLang === 'de' || selectedLang === 'ar' || selectedLang === 'ru') {
    return selectedLang
  }
  return 'en'
}

const getSortLocale = (): string => getUiLocale()

const getDisplayNames = (type: 'language' | 'region') => {
  const cacheKey = `${getUiLocale()}-${type}`
  if (displayNamesCache[cacheKey]) return displayNamesCache[cacheKey]

  const DisplayNamesCtor = (Intl as any)?.DisplayNames
  if (!DisplayNamesCtor) return null

  try {
    displayNamesCache[cacheKey] = new DisplayNamesCtor([getUiLocale()], {type})
    return displayNamesCache[cacheKey]
  } catch {
    return null
  }
}

const toLanguageTag = (normalizedCode: string): string => {
  if (!normalizedCode) return ''

  const cleaned = normalizedCode.replace(/_/g, '-')
  const parts = cleaned.split('-')

  if (parts.length === 1) {
    return parts[0].toLowerCase()
  }

  const [lang, region, ...rest] = parts
  const normalizedLang = lang.toLowerCase()
  const normalizedRegion = region ? region.toUpperCase() : ''
  const normalizedRest = rest.map((part) => (part.length === 2 ? part.toUpperCase() : part))

  return [normalizedLang, normalizedRegion, ...normalizedRest].filter((part) => part).join('-')
}

export const normalizeCode = (value?: string): string => (value || '').trim().toUpperCase()

export const formatOptionLabelWithCount = (displayName: string, count?: number): string => {
  if (count === undefined || count === null) return displayName
  return `${displayName} (${count.toLocaleString(getSortLocale())})`
}

export const getLanguageDisplayName = (code?: string): string => {
  const normalizedCode = normalizeCode(code)
  if (!normalizedCode) return LanguageHelp('Unknown')

  const displayNames = getDisplayNames('language')
  const languageTag = toLanguageTag(normalizedCode)

  try {
    const baseName = displayNames?.of(languageTag)
    if (!baseName) return normalizedCode

    return `${baseName} (${normalizedCode})`
  } catch {
    return normalizedCode
  }
}

const toRegionCode = (normalizedCode: string): string => {
  const aliased = countryCodeAlias[normalizedCode] || normalizedCode

  if (aliased.includes('-')) {
    const parts = aliased.split('-')
    const maybeRegion = parts[parts.length - 1]
    return maybeRegion.toUpperCase()
  }

  return aliased.toUpperCase()
}

export const getCountryDisplayName = (code?: string): string => {
  const normalizedCode = normalizeCode(code)
  if (!normalizedCode) return LanguageHelp('Unknown')

  const displayNames = getDisplayNames('region')
  const regionCode = toRegionCode(normalizedCode)

  try {
    const baseName = displayNames?.of(regionCode)
    if (!baseName) return normalizedCode

    return `${baseName} (${normalizedCode})`
  } catch {
    return normalizedCode
  }
}

const normalizeCountItems = (items: GeoLanguageCountryCountItem[] | string[]): GeoLanguageCountryCountItem[] => {
  if (!items?.length) return []

  if (typeof items[0] === 'string') {
    return (items as string[])
      .map((code) => normalizeCode(code))
      .filter((code) => code.length > 0)
      .map((code) => ({ code, count: 0 }))
  }

  return (items as GeoLanguageCountryCountItem[])
    .map((item) => ({
      code: normalizeCode(item.code),
      count: item.count ?? 0,
    }))
    .filter((item) => item.code.length > 0)
}

const mergeCountItems = (items: GeoLanguageCountryCountItem[]): GeoLanguageCountryCountItem[] => {
  const merged: Record<string, number> = {}

  items.forEach((item) => {
    merged[item.code] = (merged[item.code] ?? 0) + item.count
  })

  return Object.entries(merged).map(([code, count]) => ({ code, count }))
}

const sortByDisplayName = (options: GeoLanguageCountryOption[]): GeoLanguageCountryOption[] =>
  [...options].sort((a, b) => a.value.localeCompare(b.value, getSortLocale(), { sensitivity: 'base' }))

export const toLanguageOptions = (items: GeoLanguageCountryCountItem[] | string[]): GeoLanguageCountryOption[] => {
  const mergedItems = mergeCountItems(normalizeCountItems(items))

  return sortByDisplayName(
    mergedItems.map((item) => {
      const displayName = getLanguageDisplayName(item.code)
      return {
        key: item.code,
        value: displayName,
        label: formatOptionLabelWithCount(displayName, item.count),
        count: item.count,
      }
    })
  )
}

export const toCountryOptions = (items: GeoLanguageCountryCountItem[] | string[]): GeoLanguageCountryOption[] => {
  const mergedItems = mergeCountItems(normalizeCountItems(items))

  return sortByDisplayName(
    mergedItems.map((item) => {
      const displayName = getCountryDisplayName(item.code)
      return {
        key: item.code,
        value: displayName,
        label: formatOptionLabelWithCount(displayName, item.count),
        count: item.count,
      }
    })
  )
}

export const withAllLanguageOption = (options: GeoLanguageCountryOption[]): GeoLanguageCountryOption[] => [
  { key: '', value: LanguageHelp('All Languages'), label: LanguageHelp('All Languages') },
  ...options,
]

export const withAllCountryOption = (options: GeoLanguageCountryOption[]): GeoLanguageCountryOption[] => [
  { key: '', value: LanguageHelp('All Countries'), label: LanguageHelp('All Countries') },
  ...options,
]

export const getGeoOptionLabel = (options: GeoLanguageCountryOption[], selectedKey: string, allLabel: string): string => {
  if (!selectedKey) return allLabel
  return options.find((option) => option.key === selectedKey)?.label ?? allLabel
}
